export type InspectionMediaScope = {
    formId: string;
    equipmentCategoryId: string;
    unitId: string;
    mediaSessionId: string;
};

export type InspectionMediaRecordFields = InspectionMediaScope & {
    name: string;
    file: File | Blob;
    question: number;
};

export function createInspectionMediaSessionId(): string {
    return crypto.randomUUID();
}

export function buildInspectionMediaRecord(
    file: File | Blob,
    name: string,
    question: number,
    scope: InspectionMediaScope,
): InspectionMediaRecordFields {
    return {
        name,
        file,
        question,
        ...scope,
    };
}

export function matchesInspectionMediaScope(
    record: Partial<InspectionMediaScope> | null | undefined,
    scope: InspectionMediaScope,
): boolean {
    if (!record) {
        return false;
    }

    return (
        record.formId === scope.formId &&
        record.equipmentCategoryId === scope.equipmentCategoryId &&
        record.unitId === scope.unitId &&
        record.mediaSessionId === scope.mediaSessionId
    );
}

/** True when a stored record belongs to this equipment/form but not the active session. */
export function shouldPurgeInspectionMediaRecord(
    record: Partial<InspectionMediaScope & { id?: number }> | null | undefined,
    scope: InspectionMediaScope,
): boolean {
    if (!record?.formId || record.formId !== scope.formId) {
        return false;
    }

    if (record.equipmentCategoryId && record.equipmentCategoryId !== scope.equipmentCategoryId) {
        return false;
    }

    if (record.unitId && record.unitId !== scope.unitId) {
        return false;
    }

    return record.mediaSessionId !== scope.mediaSessionId;
}

export async function purgeStaleInspectionMedia(
    getAll: () => Promise<Array<Partial<InspectionMediaScope & { id?: number }>>>,
    deleteRecord: (id: number) => Promise<unknown>,
    scope: InspectionMediaScope,
): Promise<void> {
    const allRecords = await getAll();

    for (const record of allRecords) {
        if (!record?.id || !shouldPurgeInspectionMediaRecord(record, scope)) {
            continue;
        }

        try {
            await deleteRecord(record.id);
        } catch (error) {
            console.error('Failed to purge stale inspection media record', error);
        }
    }
}
